import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { MetricState } from '../../components/climateAnalytics/common'
import { CLIMATE_DATA_QUERY_KEY_ROOT, isActiveMetricState } from './index'

export type ClimateMetricStateScope =
  | { type: 'property'; propertyId: number; hazardCode?: string | null }
  | { type: 'portfolio'; hazardCode?: string | null }

export type ClimateMetricStateItem = {
  metricKey: string
  propertyId: number
  state: MetricState
}

export type ClimateMetricStatesSummary = {
  items: ClimateMetricStateItem[]
  total: number
  activeCount: number
  failedCount: number
  noDataCount: number
  noRunCount: number
  succeededCount: number
  hasActive: boolean
  /** Any non-succeeded state present. */
  hasIssues: boolean
}

type CachedClimateData = Record<number, { state?: { value?: MetricState } | null } | null>

/**
 * Derives a summary of climate metric states from the react-query cache for the
 * current scope. Only currently-observed (mounted) climate-data queries are
 * counted, so the result naturally reflects the metrics shown on the page and
 * updates as queries poll/refetch.
 */
export function useClimateMetricStates(scope: ClimateMetricStateScope): ClimateMetricStatesSummary {
  const queryClient = useQueryClient()
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const cache = queryClient.getQueryCache()
    return cache.subscribe(() => setTick(value => value + 1))
  }, [queryClient])

  const propertyId = scope.type === 'property' ? scope.propertyId : null
  const hazardCode = scope.hazardCode ?? null

  return useMemo(() => {
    const cache = queryClient.getQueryCache()
    const items: ClimateMetricStateItem[] = []

    for (const query of cache.findAll()) {
      const key = query.queryKey as unknown[]
      if (!Array.isArray(key) || key[0] !== CLIMATE_DATA_QUERY_KEY_ROOT) continue
      if (query.getObserversCount() === 0) continue

      const kind = key[1]
      if (scope.type === 'property' ? kind !== 'property' : kind !== 'portfolio') continue

      const metricKey = typeof key[2] === 'string' ? key[2] : ''
      if (hazardCode && !metricKey.startsWith(`${hazardCode}:`)) continue

      const data = query.state.data as CachedClimateData | undefined
      if (!data) continue

      if (scope.type === 'property') {
        const value = data[scope.propertyId]?.state?.value
        if (value) items.push({ metricKey, propertyId: scope.propertyId, state: value })
      } else {
        for (const [pid, entry] of Object.entries(data)) {
          const value = entry?.state?.value
          if (value) items.push({ metricKey, propertyId: Number(pid), state: value })
        }
      }
    }

    let activeCount = 0
    let failedCount = 0
    let noDataCount = 0
    let noRunCount = 0
    let succeededCount = 0
    for (const item of items) {
      if (isActiveMetricState(item.state)) activeCount += 1
      else if (item.state === MetricState.FAILED) failedCount += 1
      else if (item.state === MetricState.NO_DATA) noDataCount += 1
      else if (item.state === MetricState.NO_RUN) noRunCount += 1
      else if (item.state === MetricState.SUCCEEDED) succeededCount += 1
    }

    return {
      items,
      total: items.length,
      activeCount,
      failedCount,
      noDataCount,
      noRunCount,
      succeededCount,
      hasActive: activeCount > 0,
      hasIssues: activeCount + failedCount + noDataCount + noRunCount > 0,
    }
    // Recomputes whenever the query cache changes (`tick`) or scope changes.
  }, [queryClient, scope.type, propertyId, hazardCode, tick])
}
